import { AuthContext } from '@/contexts/authContext';
import { AUTH_CHALLENGE } from '@/enums';
import { useGetSubdomainQuery, useParseQueryString } from '@/hooks';
import {
  useMagicLogin as useMagicLinkValidate,
  useSignIn,
} from '@/queries/auth.query';
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from '@/routes/routes';
import { SignInPayload, SignInResponse } from '@/types';
import { CustomError, errorNotification } from '@/utils';
import { signInSchema } from '@/validations';
import {
  ControlledInput,
  CustomButton,
  CustomModal,
  Icon,
} from '@amili-v3/amili-v3-design';
import { useMobile } from '@amili-v3/amili-v3-design/src/hooks/useMobile';
import { InputRef, notification } from 'antd';
import dayjs from 'dayjs';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { LIMIT_WRONG_CAPTCHA } from '../OTP';

const SignIn = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setAuth } = useContext(AuthContext);

  const isMobile = useMobile();

  const { isLoading } = useGetSubdomainQuery();

  const [isOpen, setIsOpen] = useState(true);
  const emailFocusRef = useRef<InputRef>(null);
  const magicLoginAttempted = useRef(false);

  const { token } = useParseQueryString<{ token?: string }>();

  const handleWrongCaptcha = (trackingOtp: {
    count: number;
    date: string | null;
  }) => {
    const { count = 0, date = '' } = trackingOtp ?? {};
    if (count >= LIMIT_WRONG_CAPTCHA) {
      const minuteDiff = dayjs().diff(date, 'minute');
      if (minuteDiff < 5) {
        const diff = 5 - minuteDiff;
        notification.error({
          message: t('authentication.wrongCodeManyTime', {
            minute: diff,
            tail: diff > 1 ? 's' : '',
          }),
        });
        return true;
      }
    }
    return false;
  };

  const handleChallenge = (challengeName: string, accessToken?: string) => {
    switch (challengeName) {
      case AUTH_CHALLENGE.NEW_PASSWORD_REQUIRED: {
        const query = token ? `?token=${encodeURIComponent(token)}` : '';
        navigate(PUBLIC_ROUTES.CHALLENGE_RESET_PASSWORD + query);
        break;
      }
      case AUTH_CHALLENGE.PASSWORD_COMPLEXITY:
      case AUTH_CHALLENGE.PASSWORD_EXPIRATION:
        navigate(
          PUBLIC_ROUTES.PASSWORD_COMPLEXITY +
            `?accessToken=${accessToken}&challengeName=${challengeName}`,
        );
        break;
      case AUTH_CHALLENGE.SOFTWARE_TOKEN_MFA:
      case AUTH_CHALLENGE.CUSTOM_CHALLENGE:
        navigate(PUBLIC_ROUTES.OTP);
        break;
      default:
        break;
    }
  };

  const redirectIntoApp = (
    authenticationResult: SignInResponse['authenticationResult'],
  ) => {
    setAuth(authenticationResult ?? {});
    notification.success({ message: t('message.signInSuccess') });

    navigate(isMobile ? PRIVATE_ROUTES.ORDER.ROOT : PRIVATE_ROUTES.APP);
  };

  const handleSuccess = (res: { data: SignInResponse }) => {
    const { data } = res;
    const {
      trackingOtp,
      challengeParameters,
      authenticationResult,
      challengeName = null,
    } = data;

    if (!challengeParameters && !authenticationResult) {
      if (handleWrongCaptcha(trackingOtp)) {
        return;
      }
    }

    if (challengeName) {
      handleChallenge(challengeName, authenticationResult?.accessToken);
      return;
    }

    redirectIntoApp(authenticationResult);
  };

  const mutationSignIn = useSignIn({
    onSuccess: handleSuccess,
    onError: error =>
      errorNotification({ error: error as unknown as CustomError }),
  });

  const mutationMagicLinkValidate = useMagicLinkValidate({
    onSuccess: res => {
      if (!res.data.valid) {
        notification.error({ message: t('authentication.magicLinkExpired') });
        return;
      }

      handleChallenge(AUTH_CHALLENGE.NEW_PASSWORD_REQUIRED);
    },
    onError: error => {
      const statusCode = (error as unknown as CustomError)?.response?.data
        ?.statusCode;

      if (statusCode === 401) {
        notification.error({ message: t('authentication.magicLinkExpired') });
        return;
      }

      errorNotification({ error: error as unknown as CustomError });
    },
  });

  const handleSignIn = (params: SignInPayload) => mutationSignIn.mutate(params);

  useEffect(() => {
    emailFocusRef.current?.focus();
  }, []);

  useEffect(() => {
    if (token && !magicLoginAttempted.current) {
      magicLoginAttempted.current = true;
      mutationMagicLinkValidate.mutate({ token });
    }
  }, [token, mutationMagicLinkValidate]);

  return (
    <>
      <SignInWrapper
        centered
        open={isOpen}
        mask={false}
        title={<Icon name={'logoLilac'} width={107} height={36} />}
        hideCloseButton
        layout="vertical"
        formikProps={{
          enableReinitialize: true,
          validationSchema: signInSchema,
          initialValues: { email: '', password: '' },
          onSubmit: value =>
            handleSignIn({ email: value.email, password: value.password }),
        }}
        footer={[
          <CustomButton
            key="forgot-password"
            type="white"
            size="large"
            aria-label="Forgot pass btn"
            onClick={() => {
              setIsOpen(false);
              setTimeout(() => {
                navigate(PUBLIC_ROUTES.FORGOT_PASSWORD);
              }, 200);
            }}
          >
            {t('authentication.forgotPassword')}
          </CustomButton>,
          <CustomButton
            size="large"
            key="submit"
            form="modal-id"
            htmlType="submit"
            aria-label="Submit btn"
            loading={
              mutationSignIn.isPending ||
              mutationMagicLinkValidate.isPending ||
              !!isLoading
            }
          >
            {t('authentication.getMeIn')}
          </CustomButton>,
        ]}
      >
        <ControlledInput
          required
          name="email"
          ref={emailFocusRef}
          aria-label="Email ipt"
          label={t('common.email')}
        />
        <ControlledInput
          label={t('common.password')}
          name="password"
          inputType="password"
          required
          role="textbox"
          aria-label="Password inp"
        />
      </SignInWrapper>
    </>
  );
};

export default SignIn;

const SignInWrapper = styled(CustomModal)`
  .ant-modal-title {
    padding-left: 40px !important;
  }
`;
